/**
 * TOTH SPECIFICA · DASHBOARD JS
 * Editorial Luxury Minimalism · V1.0 · 2026-05-13
 * Sprint 9 · Fase 1.1
 *
 * Web Components puros (zero dependencies).
 * Regras: #privateMethod, AbortController, async/await, for...of, native APIs.
 */

(function () {
  'use strict';

  /* ════════════════════════════════════════════════════════════════════
     1. REVEAL OBSERVER — staggered fade-in (respeita reduced-motion)
     ════════════════════════════════════════════════════════════════════ */
  class TsfRevealObserver {
    static #instance = null;
    #io = null;

    static init() {
      if (TsfRevealObserver.#instance) return TsfRevealObserver.#instance;
      TsfRevealObserver.#instance = new TsfRevealObserver();
      return TsfRevealObserver.#instance;
    }

    constructor() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        for (const el of document.querySelectorAll('[data-reveal]')) {
          el.classList.add('is-visible');
        }
        return;
      }

      if (!('IntersectionObserver' in window)) {
        for (const el of document.querySelectorAll('[data-reveal]')) {
          el.classList.add('is-visible');
        }
        return;
      }

      this.#io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.#io.unobserve(entry.target);
          }
        }
      }, {
        threshold: 0.01,
        rootMargin: '0px 0px -8% 0px'
      });

      for (const el of document.querySelectorAll('[data-reveal]')) {
        this.#io.observe(el);
      }

      // Fallback: garante que tudo apareça depois de 1.5s
      window.setTimeout(() => {
        for (const el of document.querySelectorAll('[data-reveal]:not(.is-visible)')) {
          el.classList.add('is-visible');
        }
      }, 1500);
    }
  }

  /* ════════════════════════════════════════════════════════════════════
     2. <tsf-shell> — Sidebar mobile drawer + keyboard nav
     ════════════════════════════════════════════════════════════════════ */
  class TsfShell extends HTMLElement {
    #toggleBtn = null;
    #sidebarEl = null;
    #dashEl = null;
    #abortController = null;

    connectedCallback() {
      this.#toggleBtn = this.querySelector('[data-tsf-sidebar-toggle]');
      this.#sidebarEl = this.querySelector('[data-tsf-sidebar]');
      // 12-07: o drawer mobile abre via .tsf-dashboard[data-sidebar-open] (o CSS mira o wrapper, não o <tsf-shell>).
      this.#dashEl = this.closest('.tsf-dashboard') || this;
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      if (this.#toggleBtn) {
        this.#toggleBtn.addEventListener('click', this.#toggleSidebar.bind(this), { signal });
      }

      // Fecha sidebar ao clicar fora (mobile)
      this.addEventListener('click', (e) => {
        if (!this.#dashEl.hasAttribute('data-sidebar-open')) return;
        if (this.#sidebarEl?.contains(e.target)) return;
        if (this.#toggleBtn?.contains(e.target)) return;
        this.#closeSidebar();
      }, { signal });

      // Escape fecha sidebar mobile
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.#dashEl.hasAttribute('data-sidebar-open')) {
          this.#closeSidebar();
          this.#toggleBtn?.focus();
        }
      }, { signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #toggleSidebar() {
      if (this.#dashEl.hasAttribute('data-sidebar-open')) {
        this.#closeSidebar();
      } else {
        this.#openSidebar();
      }
    }

    #openSidebar() {
      this.#dashEl.setAttribute('data-sidebar-open', '');
      this.#toggleBtn?.setAttribute('aria-expanded', 'true');
      // Focus first link for keyboard users
      const firstLink = this.#sidebarEl?.querySelector('a, button');
      window.requestAnimationFrame(() => firstLink?.focus());
    }

    #closeSidebar() {
      this.#dashEl.removeAttribute('data-sidebar-open');
      this.#toggleBtn?.setAttribute('aria-expanded', 'false');
    }
  }

  /* ════════════════════════════════════════════════════════════════════
     3. <tsf-copy> — Copy-to-clipboard helper (coupons, codes)
     ════════════════════════════════════════════════════════════════════ */
  class TsfCopy extends HTMLElement {
    #abortController = null;

    connectedCallback() {
      const button = this.querySelector('[data-tsf-copy-button]');
      const text = this.dataset.tsfCopyValue;
      if (!button || !text) return;

      this.#abortController = new AbortController();
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(text);
          this.#showFeedback(button, 'Copiado');
        } catch {
          this.#showFeedback(button, 'Selecione manualmente');
        }
      }, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #showFeedback(button, message) {
      const original = button.querySelector('[data-tsf-copy-label]')?.textContent ?? button.textContent;
      const labelEl = button.querySelector('[data-tsf-copy-label]') ?? button;
      labelEl.textContent = message;
      button.setAttribute('data-tsf-copy-done', '');
      window.setTimeout(() => {
        labelEl.textContent = original;
        button.removeAttribute('data-tsf-copy-done');
      }, 2000);
    }
  }

  /* ════════════════════════════════════════════════════════════════════
     4. <tsf-supplier-drawer> — Drawer luxury para detalhe de fornecedor
     URL hash deep-link: #fornecedor=zomer
     ════════════════════════════════════════════════════════════════════ */
  class TsfSupplierDrawer extends HTMLElement {
    #abortController = null;
    #panels = null;
    #lastFocused = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#panels = this.querySelectorAll('[data-supplier-handle]');

      // Click no card abre drawer
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-tsf-supplier-trigger]');
        if (trigger) {
          e.preventDefault();
          const handle = trigger.dataset.tsfSupplierTrigger;
          this.#openDrawer(handle);
        }
      }, { signal });

      // Botão close
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-tsf-drawer-close]')) {
          this.#closeDrawer();
        }
        if (e.target.classList.contains('tsf-supplier-drawer__backdrop')) {
          this.#closeDrawer();
        }
      }, { signal });

      // Escape fecha
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('data-open')) {
          this.#closeDrawer();
        }
      }, { signal });

      // URL hash on load + change
      this.#syncFromHash();
      window.addEventListener('hashchange', () => this.#syncFromHash(), { signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #syncFromHash() {
      const match = window.location.hash.match(/fornecedor=([^&]+)/);
      if (match && match[1]) {
        this.#openDrawer(decodeURIComponent(match[1]), false);
      } else if (this.hasAttribute('data-open')) {
        this.#closeDrawer(false);
      }
    }

    #openDrawer(handle, updateHash = true) {
      if (!handle) return;
      const panel = this.querySelector(`[data-supplier-handle="${handle}"]`);
      if (!panel) {
        console.warn('[Toth Specifica] Fornecedor não encontrado:', handle);
        return;
      }

      // Esconde outros panels, mostra o ativo
      for (const p of this.#panels) {
        p.hidden = p !== panel;
      }

      this.#lastFocused = document.activeElement;
      this.setAttribute('data-open', '');
      this.setAttribute('aria-modal', 'true');
      document.body.style.overflow = 'hidden';

      // Update URL hash sem recarregar
      if (updateHash) {
        history.replaceState(null, '', `#fornecedor=${encodeURIComponent(handle)}`);
      }

      // Focus no close button
      window.requestAnimationFrame(() => {
        const closeBtn = this.querySelector('[data-tsf-drawer-close]');
        closeBtn?.focus();
      });

      // Track via dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'SpecificaSupplierView',
        supplier_handle: handle
      });
    }

    #closeDrawer(updateHash = true) {
      this.removeAttribute('data-open');
      this.removeAttribute('aria-modal');
      document.body.style.overflow = '';

      if (updateHash && window.location.hash.includes('fornecedor=')) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      // Return focus to trigger
      this.#lastFocused?.focus();
    }
  }

  /* ════════════════════════════════════════════════════════════════════
     5. AUTO-INIT on DOM ready
     ════════════════════════════════════════════════════════════════════ */
  function init() {
    // Register web components if not already defined
    if (!customElements.get('tsf-shell')) {
      customElements.define('tsf-shell', TsfShell);
    }
    if (!customElements.get('tsf-copy')) {
      customElements.define('tsf-copy', TsfCopy);
    }
    if (!customElements.get('tsf-supplier-drawer')) {
      customElements.define('tsf-supplier-drawer', TsfSupplierDrawer);
    }
    // Init reveal observer
    TsfRevealObserver.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
